import { useCallback, useState } from "react";

/**
 * A hook for managing boolean toggle state.
 * Provides a clean API for open/close, show/hide, or enable/disable patterns.
 *
 * @param initialValue - Initial boolean state (default: false)
 * @returns Tuple of [value, { toggle, setTrue, setFalse, setValue }]
 *
 * @example
 * // Basic toggle
 * const [isOpen, { toggle, setTrue: open, setFalse: close }] = useToggle();
 *
 * @example
 * // With initial value
 * const [isVisible, { toggle }] = useToggle(true);
 *
 * @example
 * // Modal pattern
 * const [isModalOpen, modalControls] = useToggle();
 * <button onClick={modalControls.setTrue}>Open Modal</button>
 * <Modal open={isModalOpen} onClose={modalControls.setFalse} />
 */
export function useToggle(initialValue = false): [
  boolean,
  {
    /** Toggle the value */
    toggle: () => void;
    /** Set value to true */
    setTrue: () => void;
    /** Set value to false */
    setFalse: () => void;
    /** Set to a specific value */
    setValue: (value: boolean) => void;
  },
] {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => setValue((v) => !v), []);
  const setTrue = useCallback(() => setValue(true), []);
  const setFalse = useCallback(() => setValue(false), []);

  return [
    value,
    {
      toggle,
      setTrue,
      setFalse,
      setValue,
    },
  ];
}
